import React, { Component, Fragment } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import {
  DatePicker, Popconfirm, Select, Upload,
} from 'antd';
import { v4 as uuidv4 } from 'uuid';
import dayjs from 'dayjs';
import { PlusOutlined } from '@ant-design/icons';
import Constants, { QUANTITY, DEFAULT_CUR_ROUND_OFF } from '@Apis/constants';
import H3Text from '@Uilib/h3Text';
import H3Button, { defaultButtonTypes } from '@Uilib/h3Button';
import CFV2Actions from '@Actions/configurations/cfV2Actions';
import PurchaseRequestActions from '@Actions/purchaseRequestActions';
import CustomFieldV3 from '@Components/Common/CustomFieldV3';
import PurchaseRequestLines from './PurchaseRequestLines';
import './style.scss';
import TagActions from '@Actions/tagActions';
import TagSelector from '@Components/Common/Selector/TagSelector';
import Crown from '@Images/crown2.png';
import CustomFieldHelpers from '@Helpers/CustomFieldHelpers';
import FormHelpers from '@Helpers/FormHelpers';
import CustomDocumentInputs from '@Components/Common/CustomDocumentInputs';
import CategoryFilterNew from '../../../../../Common/CategoryFilterNew';
import documentUINamesConstant from '../../../../../../apis/documentUINamesConstant';
import RestrictedAccessMessage from '../../../../../Common/RestrictedAccess/RestrictedAccessMessage';
import PRZSelect from '../../../../../Common/UI/PRZSelect';
import DocumentNumberSeqInput from '../../../../../Admin/Common/DocumentNumberSeqInput';

const { Option } = Select;

const uploadButton = (
  <div>
    <PlusOutlined />
    <div style={{ marginTop: 8 }}>Upload</div>
  </div>
);

class CreatePurchaseRequestFromMo extends Component {
  constructor(props) {
    super(props);
    this.state = {
      deliveryDate: dayjs(),
      showFulfilledLines: true,
      documentUINames: {},
      visibleColumns: {
        PRODUCT: {
          label: 'Product',
          visible: true,
          disabled: true,
        },
        FG_SFG: {
          label: 'FG/SFG',
          visible: true,
          disabled: true,
        },
        QUANTITY: {
          label: 'Quantity',
          visible: true,
          disabled: true,
        },
        AVAILABLE_QTY: {
          label: 'Available Quantity',
          visible: false,
          disabled: true,
        },
        UNIT_PRICE: {
          label: 'Rate',
          visible: false,
          disabled: true,
        },
        TARGET_PRICE: {
          label: 'Target Price',
          visible: false,
          disabled: true,
        },
        AVAILABLE_STOCK: {
          label: 'Available Stock',
          visible: true,
          disabled: true,
        },
        SHORT_QTY: {
          label: 'Short Quantity',
          visible: true,
          disabled: true,
        },
        BOM_QTY: {
          label: 'BOM Quantity',
          visible: true,
          disabled: true,
        },
        UNIT: {
          label: 'Unit',
          visible: true,
          disabled: true,
        },
      },
    };
  }

  componentDidMount() {
    const {
      getDocCFV2, user,
    } = this.props;
    const payload = {
      orgId: user?.tenant_info?.org_id,
      entityName: 'PURCHASE_REQUEST',
    };
    getDocCFV2(payload);

    this.setState({
      documentUINames: documentUINamesConstant(user),
    });
  }

  handleDelete = (key) => {
    const { moLines, soLines, copyMoLines } = this.state;
    if (moLines?.length > 1) {
      const storedRemovedKeys = JSON.parse(sessionStorage.getItem('removed_line_keys_mo_to_pr')) || [];
      // Merge current and previously removed keys
      const updatedRemovedKeys = [...new Set([...storedRemovedKeys, key])];

      // Store updated removed keys in sessionStorage
      sessionStorage.setItem('removed_line_keys_mo_to_pr', JSON.stringify(updatedRemovedKeys));
      // const copyData = moLines.filter((item) => item.key !== key);
      const filteredData = copyMoLines?.filter((item) => !updatedRemovedKeys?.includes(item.key));
      const newFilterOptions = [
        ...new Map(
          filteredData
            ?.filter(
              (item) => item?.tenant_product_info?.product_category_id // Ensure `product_category_id` exists
                && item?.tenant_product_info?.category_path?.length > 0, // Ensure `category_path` is not empty
            )
            .map((item) => [
              item?.tenant_product_info?.product_category_id, // Use `id` as the key to ensure uniqueness
              {
                id: item?.tenant_product_info?.product_category_id,
                name: item?.tenant_product_info?.category_path[
                  item?.tenant_product_info?.category_path.length - 1
                ],
              },
            ]),
        ).values(),
      ];
      this.setState({ moLines: filteredData, optionsForProductCategoryFilter: newFilterOptions, selectedProductCategory: [] });
    }
    if (soLines?.length > 1) {
      const copyData = soLines.filter((item) => item.key !== key);
      this.setState({ soLines: copyData });
    }
  }

  static getDerivedStateFromProps(props, state) {
    const {
      selectedMO, selectedSO, user, cfV2DocPurchaseRequest
    } = props;
    const {
      showFulfilledLines, documentUINames, visibleColumns,
    } = state;
    const allowExcessQtyForSoToPr = user?.tenant_info?.purchase_config?.sub_modules?.purchase_request?.settings?.allow_excess_quantity_for_so_to_pr;
    if (selectedMO?.mo_id && !state.isUserReady) {
      const moLines = selectedMO?.mo_lines
        ?.map((item) => ({
          ...item,
          key: uuidv4(),
          available_quantity: item?.tenant_product_info?.total_available_batches_quantity,
          shortfall: QUANTITY((item?.quantity + item?.wastage_quantity - Math.abs(item?.issued_quantity) - item?.tenant_product_info?.total_available_batches_quantity) > 0 ? (item?.quantity + item?.wastage_quantity - Math.abs(item?.issued_quantity) - item?.tenant_product_info?.total_available_batches_quantity) : '0', item?.uom_info?.precision),
          quantity: QUANTITY((item?.quantity + item?.wastage_quantity - Math.abs(item?.issued_quantity) - item?.tenant_product_info?.total_available_batches_quantity) > 0 ? (item?.quantity + item?.wastage_quantity - Math.abs(item?.issued_quantity) - item?.tenant_product_info?.total_available_batches_quantity) : '0', item?.uom_info?.precision),
          bomQuantity: QUANTITY((item?.quantity + (item?.wastage_quantity || 0)), item?.uom_info?.precision),
          productCategoryId: item?.tenant_product_info?.product_category_id ?? 'null',
        }));

      if (props.cfV2DocPurchaseRequest) {
        const mergedDocCustomFields = cfV2DocPurchaseRequest?.data?.document_custom_fields?.map((currField) => {
          const moField = selectedMO?.custom_fields?.find((item) => { return (currField?.cf_id === item?.cf_id) });
          return {
            ...currField,
            field_value: moField?.field_value || currField?.field_value,
          }
        });

        return {
          ...state,
          isUserReady: true,

          moLines: Object.values(moLines)?.map(line => {
            const mergedLineCustomFields = cfV2DocPurchaseRequest?.data?.document_line_custom_fields?.map((customField) => {
              const moField = line?.custom_fields?.find((item) => (item?.cf_id === customField?.cf_id));
              return {
                ...customField,
                field_value: moField?.field_value || customField?.field_value,
              }
            })
            return {
              ...line,
              remarks: line?.tenant_product_info?.description?.replace(/<[^>]+>/g, ''),
              custom_fields: mergedLineCustomFields,
              mo_line_custom_fields: mergedLineCustomFields,
              lineCustomFields: CustomFieldHelpers.getCfStructure(mergedLineCustomFields),
            }
          }),
          copyMoLines: Object.values(moLines)?.map(line => {
            const mergedLineCustomFields = cfV2DocPurchaseRequest?.data?.document_line_custom_fields?.map((customField) => {
              const moField = line?.custom_fields?.find((item) => (item?.cf_id === customField?.cf_id));
              return {
                ...customField,
                field_value: moField?.field_value || customField?.field_value,
              }
            })
            return {
              ...line,
              remarks: line?.tenant_product_info?.description?.replace(/<[^>]+>/g, ''),
              custom_fields: mergedLineCustomFields,
              mo_line_custom_fields: mergedLineCustomFields,
              lineCustomFields: CustomFieldHelpers.getCfStructure(mergedLineCustomFields),
            }
          }),
          optionsForProductCategoryFilter: [
            ...new Map(
              Object.values(moLines)
                .filter(
                  (line) => line?.tenant_product_info?.product_category_id
                    && line?.tenant_product_info?.category_path?.length > 0,
                )
                .map((line) => [
                  line?.tenant_product_info?.product_category_id,
                  {
                    id: line?.tenant_product_info?.product_category_id,
                    name: line?.tenant_product_info?.category_path[line?.tenant_product_info?.category_path.length - 1],
                  },
                ]),
            ).values(),
          ],
          remark: `These Raw Materials are required for Manufacturing Order #${selectedMO?.mo_number}`,
          cfPurchaseRequestDoc: CustomFieldHelpers.getCfStructure(mergedDocCustomFields, true) || [],
          visibleColumns: {
            ...CustomFieldHelpers.updateVisibleColumns(cfV2DocPurchaseRequest?.data?.document_line_custom_fields, visibleColumns),
          },
          cfPurchaseRequestLine: CustomFieldHelpers.getCfStructure(cfV2DocPurchaseRequest?.data?.document_line_custom_fields?.filter((item) => item?.is_active), false),
        };
      }
    }

    const getSoPrLines = (soLines) => {
      const lines = [];
      for (let i = 0; i < soLines?.length; i++) {
        const item = soLines[i];
        if (item?.product_sku_info?.product_type === 'BUNDLE') {
          for (let j = 0; j < item?.bundle_products?.length; j++) {
            const bundleItem = item?.bundle_products[j];
            lines.push({
              ...bundleItem,
              key: uuidv4(),
              available_quantity: bundleItem?.available_batches?.map((batch) => batch.available_qty).reduce((acc, val) => acc + val, 0) || 0,
              shortfall: QUANTITY(((bundleItem.quantity) - (bundleItem?.available_batches?.map((batch) => batch.available_qty).reduce((acc, val) => acc + val, 0) || 0)) > 0 ? ((bundleItem.quantity) - (bundleItem?.available_batches?.map((batch) => batch.available_qty).reduce((acc, val) => acc + val, 0) || 0)) : '0', bundleItem?.uom_info?.[0]?.precision),
              pr_quantity: QUANTITY((bundleItem.quantity) - (bundleItem?.available_batches?.map((batch) => batch.available_qty).reduce((acc, val) => acc + val, 0) || 0) > 0 ? ((bundleItem.quantity) - (bundleItem?.available_batches?.map((batch) => batch.available_qty).reduce((acc, val) => acc + val, 0) || 0)) : '0', bundleItem?.uom_info?.[0]?.precision),
            });
          }
        } else {
          lines.push({
            ...item,
            key: uuidv4(),
            available_quantity: item?.available_batches?.map((batch) => batch.available_qty).reduce((acc, val) => acc + val, 0) || 0,
            shortfall: QUANTITY(((item.quantity) - (item?.available_batches?.map((batch) => batch.available_qty).reduce((acc, val) => acc + val, 0) || 0)) > 0 ? ((item.quantity) - (item?.available_batches?.map((batch) => batch.available_qty).reduce((acc, val) => acc + val, 0) || 0)) : '0', item?.uom_info?.[0]?.precision),
            pr_quantity: QUANTITY((item.quantity) - (item?.available_batches?.map((batch) => batch.available_qty).reduce((acc, val) => acc + val, 0) || 0) > 0 ? ((item.quantity) - (item?.available_batches?.map((batch) => batch.available_qty).reduce((acc, val) => acc + val, 0) || 0)) : '0', item?.uom_info?.[0]?.precision),
          });
        }
      }
      return lines;
    };

    if (selectedSO?.order_id && !state.isUserReady) {
      const soLines = (allowExcessQtyForSoToPr && !showFulfilledLines) ? getSoPrLines(selectedSO?.sales_order_lines) : getSoPrLines(selectedSO?.sales_order_lines
        ?.filter((line) => (((line?.quantity) - (line?.available_batches?.map((batch) => batch.available_qty).reduce((acc, val) => acc + val, 0) || 0)) > 0)));
      if (props.cfV2DocPurchaseRequest) {
        const mergedDocCustomFields = cfV2DocPurchaseRequest?.data?.document_custom_fields?.map((currField) => {
          const soField = selectedSO?.custom_fields?.find((item) => { return (currField?.cf_id === item?.cf_id) });
          return {
            ...currField,
            field_value: soField?.field_value || currField?.field_value,
          }
        });

        return {
          ...state,
          isUserReady: true,
          soLines: soLines.map(line => {
            const mergedLineCustomFields = cfV2DocPurchaseRequest?.data?.document_line_custom_fields?.map((customField) => {
              const soField = line?.custom_fields?.find((item) => { return (item?.cf_id === customField?.cf_id) });
              return {
                ...customField,
                field_value: soField?.field_value || customField?.field_value,
              }
            })
            return {
              ...line,
              remarks: line?.remarks || line?.tenant_product_info?.description?.replace(/<[^>]+>/g, ''),
              custom_fields: mergedLineCustomFields,
              so_line_custom_fields: mergedLineCustomFields,
              lineCustomFields: CustomFieldHelpers.getCfStructure(mergedLineCustomFields),
            }
          }),
          remark: `These Products are required for ${documentUINames?.salesOrderUIName || 'Sales Order'} #${selectedSO?.sales_order_number}`,
          tenantDepartmentId: selectedSO?.tenant_department_id,
          selectedTags: selectedSO?.tags || [],
          cfPurchaseRequestDoc: CustomFieldHelpers.getCfStructure(mergedDocCustomFields, true) || [],
          visibleColumns: {
            ...CustomFieldHelpers.updateVisibleColumns(cfV2DocPurchaseRequest?.data?.document_line_custom_fields, visibleColumns),
          },
          cfPurchaseRequestLine: CustomFieldHelpers.getCfStructure(cfV2DocPurchaseRequest?.data?.document_line_custom_fields?.filter((item) => item?.is_active), false),
        };
      }
    }
    return state;
  }

  isDataValid() {
    const {
      moLines, deliveryDate, soLines, tenantDepartmentId, mrNumber,
    } = this.state;
    const {
      user,
    } = this.props;
    let isDataValid = true;
    const allowExcessQtyForSoToPr = user?.tenant_info?.purchase_config?.sub_modules?.purchase_request?.settings?.allow_excess_quantity_for_so_to_pr;
    if ((!moLines?.length || moLines.length === 0) && (!soLines?.length || soLines.length === 0) || !mrNumber) {
      return false;
    }
    if (moLines?.length) {
      for (let i = 0; i < moLines.length; i++) {
        if (Number(moLines[i].quantity) <= 0 || !moLines[i].tenant_product_info?.product_sku_name || !moLines[i]?.uom_id) {
          isDataValid = false;
        }
      }
    } else if (soLines?.length) {
      for (let i = 0; i < soLines?.length; i++) {
        if (Number(soLines[i]?.pr_quantity) <= 0 || !soLines[i]?.product_sku_info?.product_sku_name || !soLines[i]?.uom_id || !tenantDepartmentId) {
          isDataValid = false;
        }
        if ((Number(soLines[i]?.pr_quantity) > Number(soLines[i]?.shortfall)) && !allowExcessQtyForSoToPr) {
          isDataValid = false;
        }
      }
    }

    if (!deliveryDate) isDataValid = false;
    return isDataValid;
  }

  createPurchaseRequest(withApproval) {
    this.setState({ withApproval, actionType: withApproval ? 'SAVE_AND_ISSUE' : 'SAVE_AS_DRAFT' });
    const {
      createPurchaseRequest, callback, user, selectedMO, selectedSO, createTag,
    } = this.props;
    const {
      deliveryDate, moLines, remark, fileList, soLines, tenantDepartmentId, cfPurchaseRequestDoc, selectedTags, mrNumber, docSeqId, initialMrNumber,
    } = this.state;
    const purchaseRequestLines = [];
    if (moLines?.length) {
      this.setState({ formSubmitted: true, moLines: JSON.parse(JSON.stringify(moLines)) });
    } else if (soLines?.length) {
      this.setState({ formSubmitted: true, soLines: JSON.parse(JSON.stringify(soLines)) });
    }
    if (this.isDataValid() && !cfPurchaseRequestDoc?.filter((customField) => customField.isActive && customField.isRequired && (customField?.fieldType === 'ATTACHMENT' ? !customField?.fieldValue?.length : !customField?.fieldValue))?.length) {
      let payload;
      if (moLines?.length > 0) {
        for (let i = 0; i < moLines.length; i++) {
          const prLine = {
            tenant_product_id: moLines?.[i].tenant_product_id,
            product_sku_name: moLines?.[i].tenant_product_info?.product_sku_name,
            product_sku_id: moLines?.[i].tenant_product_info?.product_sku_id,
            uom_id: moLines?.[i]?.uom_info?.uom_id,
            uom_info: moLines?.[i]?.uom_info,
            quantity: Number(moLines?.[i]?.quantity),
            mo_line_id: moLines?.[i].mo_line_id,
            remark: moLines?.[i].remarks?.replace(/<[^>]+>/g, ''),
            custom_fields: CustomFieldHelpers.postCfStructure(moLines[i]?.lineCustomFields),
          };
          purchaseRequestLines.push(prLine);
        }
        payload = {
          tenant_id: selectedMO?.tenant_id,
          purchase_requisition_lines: purchaseRequestLines,
          mo_id: selectedMO?.mo_id,
          remark,
          pr_date: dayjs().format('YYYY/MM/DD'),
          delivery_date: dayjs(deliveryDate).format('YYYY/MM/DD'),
          status: withApproval ? 'ISSUED' : 'DRAFT',
          attachments: fileList?.map((attachment) => ({
            url: attachment?.response?.response?.location || attachment?.url, type: attachment.type, name: attachment.name, uid: attachment.uid,
          })) || [],
          tenant_department_id: selectedMO?.rm_tenant_department_id,
          custom_fields: CustomFieldHelpers.postCfStructure(cfPurchaseRequestDoc),
          pr_number: initialMrNumber?.toLowerCase()?.trim() === mrNumber?.toLowerCase()?.trim() ? null : mrNumber,
          seq_id: docSeqId || null,
        };
      }
      if (soLines?.length > 0) {
        for (let i = 0; i < soLines.length; i++) {
          const prLine = {
            tenant_product_id: soLines?.[i].tenant_product_id,
            product_sku_name: soLines?.[i].product_sku_info?.product_sku_name,
            product_sku_id: soLines?.[i].product_sku_info?.product_sku_id,
            uom_id: soLines?.[i]?.uom_info?.[0]?.uom_id,
            uom_info: soLines?.[i]?.uom_info?.[0],
            quantity: Number(soLines?.[i]?.pr_quantity),
            order_line_id: soLines?.[i].order_line_id,
            remark: soLines?.[i].remarks?.replace(/<[^>]+>/g, ''),
            custom_fields: CustomFieldHelpers.postCfStructure(soLines[i]?.lineCustomFields),
          };
          purchaseRequestLines.push(prLine);
        }
        payload = {
          tenant_id: selectedSO?.tenant_id,
          purchase_requisition_lines: purchaseRequestLines,
          order_id: selectedSO?.order_id,
          remark,
          pr_date: dayjs().format('YYYY/MM/DD'),
          delivery_date: dayjs(deliveryDate).format('YYYY/MM/DD'),
          status: withApproval ? 'ISSUED' : 'DRAFT',
          attachments: fileList?.map((attachment) => ({
            url: attachment?.response?.response?.location || attachment?.url, type: attachment.type, name: attachment.name, uid: attachment.uid,
          })) || [],
          tenant_department_id: tenantDepartmentId,
          custom_fields: CustomFieldHelpers.postCfStructure(cfPurchaseRequestDoc),
          pr_number: initialMrNumber?.toLowerCase()?.trim() === mrNumber?.toLowerCase()?.trim() ? null : mrNumber,
          seq_id: docSeqId || null,
        };
      }
      createPurchaseRequest([payload], (createdPr) => {
        if (selectedTags?.length) {
          const tagPayload = {
            entity_name: 'PURCHASE_REQUISITION',
            entity_id: createdPr?.purchase_requisitions?.[0]?.purchase_requisition_id,
            action: 'ADD',
            tag: selectedTags.join(','),
          };
          createTag(tagPayload, () => {
            this.setState({ currentAction: false, actionType: '' });
            callback();
          });
        } else {
          this.setState({ currentAction: false, actionType: '' });
          callback();
        }
      });
    }
  }

  customInputChange(fieldValue, cfId) {
    const { cfPurchaseRequestDoc } = this.state;
    const newCustomField = cfPurchaseRequestDoc.map((customField) => {
      if (customField?.cfId === cfId) {
        if (customField?.fieldType === 'ATTACHMENT') {
          return {
            ...customField,
            fieldValue: fieldValue?.map((attachment) => ({
              url: attachment?.response?.response?.location || attachment?.url, type: attachment.type, name: attachment.name, uid: attachment.uid,
            })),

          };
        }
        return {
          ...customField,
          fieldValue,
        };
      }
      return {
        ...customField,
      };
    });
    this.setState({
      cfPurchaseRequestDoc: newCustomField,
    });
  }

  customLineInputChange(fieldValue, key) {
    const { moLines, soLines } = this.state;
    if (moLines?.length > 0) {
      const copyData = JSON.parse(JSON.stringify(moLines));
      for (let i = 0; i < copyData.length; i++) {
        if (copyData[i]?.key === key) {
          copyData[i].lineCustomFields = fieldValue;
          const qtyCf = fieldValue?.find((j) => j?.fieldName === 'Quantity');
          copyData[i].quantity = qtyCf?.fieldValue > 0 ? parseFloat(Number(qtyCf?.fieldValue).toFixed(DEFAULT_CUR_ROUND_OFF)) : '';
        }
      }
      this.setState({ moLines: copyData });
    } else {
      const copyData = JSON.parse(JSON.stringify(soLines));
      for (let i = 0; i < copyData.length; i++) {
        if (copyData[i]?.key === key) {
          copyData[i].lineCustomFields = fieldValue;
          const qtyCf = fieldValue?.find((j) => j?.fieldName === 'Quantity');
          copyData[i].pr_quantity = qtyCf?.fieldValue > 0 ? parseFloat(Number(qtyCf?.fieldValue).toFixed(DEFAULT_CUR_ROUND_OFF)) : '';
        }
      }
      this.setState({ soLines: copyData });
    }
  }

  render() {
    const {
      createPurchaseRequestLoading, selectedSO, user, selectedMO,
    } = this.props;
    const {
      remark, formSubmitted, deliveryDate, moLines, fileList, actionType, soLines, tenantDepartmentId,
      showFulfilledLines, isUserReady, cfPurchaseRequestDoc, selectedTags, copyMoLines,
      optionsForProductCategoryFilter, selectedProductCategory, cfPurchaseRequestLine, visibleColumns, docSeqId, mrNumber,
    } = this.state;

    const purchaseRequest = user?.tenant_info?.purchase_config?.sub_modules?.purchase_request?.is_active;

    return (
      <Fragment>
        <Fragment>
          <div
            className="form__wrapper"
            style={{
              paddingTop: '0px',
            }}
          >
            <div className="ant-row">
              <div className="ant-col-md-24">
                <div className="form__section">
                  <div className="flex-display flex-align-c mg-bottom-5 pd-right-15">
                    <H3Text text="PART A" className="form__section-title" />
                    <div className="form__section-line" />
                    <div style={{
                      display: 'flex', justifyContent: 'end', alignItems: 'center', marginLeft: '10px',
                    }}
                    >
                      <CustomDocumentInputs
                        customFields={cfPurchaseRequestDoc}
                        updateCustomFields={(cf) => this.setState({ cfPurchaseRequestDoc: cf })}
                      />
                    </div>
                  </div>
                  <div className="form__section-inputs mg-bottom-20">
                    <div className="ant-row">
                      <DocumentNumberSeqInput
                        valueFromProps={mrNumber}
                        setInitialDocSeqNumber={(value) => this.setState({ initialMrNumber: value })}
                        entityName="MATERIAL_REQUEST"
                        docSeqId={docSeqId}
                        tenantId={user?.tenant_info?.tenant_id}
                        onChangeFromProps={(event, newValue, seqId) => {
                          this.setState({
                            mrNumber: newValue ? (newValue || '') : (event?.target?.value || ''),
                            docSeqId: seqId,
                          })
                        }}
                        docTitle="Material Request#"
                        formSubmitted={formSubmitted}
                      />
                      <div className="ant-col-md-6">
                        <div className="form__input-row orgInputContainer">
                          <H3Text
                            text="Delivery Date"
                            className="form__input-row__label"
                          />
                          <div className="form__input-row__input">
                            <DatePicker
                              value={deliveryDate}
                              onChange={(value) => {
                                this.setState({ deliveryDate: value });
                              }}
                              disabled={createPurchaseRequestLoading}
                              style={{
                                border: '1px solid rgba(68, 130, 218, 0.2)',
                                borderColor: formSubmitted && !deliveryDate ? 'red' : 'rgba(68, 130, 218, 0.2)',
                                borderRadius: '5px',
                                height: '28px',
                                padding: '1px 3px',
                                width: '100%',
                                background: 'white',
                                marginBottom: '10px',
                              }}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="ant-col-md-6">
                        <div className="form__input-row ">
                          <div className="form__input-row__label">
                            Labels
                          </div>
                          <TagSelector
                            hideTitle
                            entityType="PURCHASE_REQUISITION"
                            selectedTags={selectedTags}
                            isMultiple
                            showSearch
                            onChange={(value) => {
                              this.setState({ selectedTags: value });
                            }}
                            placeholder="Select Tags"
                            isForm
                            containerWrapper="form__input-row__input"
                            disabled={createPurchaseRequestLoading}
                            maxTagCount="responsive"
                          />
                        </div>
                      </div>
                      <CustomFieldV3
                        customFields={cfPurchaseRequestDoc || []}
                        formSubmitted={formSubmitted}
                        customInputChange={(value, cfId) => this.customInputChange(value, cfId)}
                        disableCase={createPurchaseRequestLoading}
                        hideTitle
                        isCarryForward
                        wrapperClassName="ant-col-md-6"
                        containerClassName="form__input-row"
                        labelClassName="form__input-row__label"
                        inputClassName="form__input-row__input"
                        errorClassName="form__input-row__input-error"
                      />
                    </div>
                  </div>
                </div>
                <div className="form__section ">
                  <div className="flex-display flex-align-c mg-bottom-5 pd-right-15">
                    <H3Text text="PART B" className="form__section-title " />
                    <div className="form__section-line" />
                  </div>
                  <div className="ant-row">
                    <div className="form__input-row">
                      <H3Text
                        text="Remarks"
                        className="form__input-row__label"
                      />
                      <div className="form__input-row__input">
                        <textarea
                          rows="5"
                          cols="50"
                          onChange={(event) => {
                            this.setState({ remark: event.target.value });
                          }}
                          disabled={createPurchaseRequestLoading}
                          value={remark}
                          style={{
                            resize: 'none',
                            backgroundColor: 'rgb(239, 239, 239)',
                            border: 'none',
                            borderRadius: '4px',
                            marginRight: '15px',
                            height: '103px',
                            padding: '4px 8px',
                          }}
                        />
                      </div>
                    </div>
                    <div className="ant-col-md-12">
                      <div
                        className="form__data-attachment"
                      >
                        <label className="form__input-row__label">Attachment(s)</label>
                        <Upload
                          action={Constants.UPLOAD_FILE}
                          listType="picture-card"
                          fileList={fileList}
                          disabled={createPurchaseRequestLoading}
                          multiple
                          onChange={(fileListData) => {
                            this.setState({
                              fileList: fileListData?.fileList?.map((item) => ({
                                ...item,
                                url: item?.response?.response?.location,
                              })),
                            });
                          }}
                        >
                          {fileList?.length >= 20 ? null : uploadButton}
                        </Upload>
                      </div>
                    </div>
                  </div>
                </div>
                {selectedMO && (
                  <div className="form__section ">
                    <div className="flex-display flex-align-c mg-bottom-5 pd-right-15">
                      <H3Text text="Filters" className="form__section-title " />
                      <div className="form__section-line" />
                    </div>
                    <div className="ant-row">
                      <div className="ant-col-md-8">
                        <div
                          className="form__data-attachment product_category_filter"
                        >
                          <label className="form__input-row__label">Product Category</label>
                          <PRZSelect
                            placeholder="Select a product category"
                            hideTitle
                            showSearch
                            mode="multiple"
                            filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                            maxTagCount="responsive"
                            style={{
                              height: '28px',
                              borderRadius: '4px',
                              backgroundColor: '#efefef',
                              border: 'none',
                              fontSize: '12px',
                            }}
                            value={selectedProductCategory}
                            onChange={(value) => {
                              this.setState({ selectedProductCategory: value });
                              const storedRemovedKeys = JSON.parse(sessionStorage.getItem('removed_line_keys_mo_to_pr')) || [];

                              if (value?.length > 0) {

                                const searchCriteria = {
                                  productCategoryId: value,
                                };
                                const excludeKeys = ['child_mo_lines'];
                                let matchedRows;

                                if (copyMoLines) {
                                  const filteredData = copyMoLines?.filter(
                                    (item) => !storedRemovedKeys.includes(item.key),
                                  );
                                  matchedRows = FormHelpers.findMatchingRows(
                                    filteredData,
                                    searchCriteria,
                                    excludeKeys,
                                  );
                                }

                                this.setState({ moLines: matchedRows || [] });
                              } else {
                                const filteredData = copyMoLines?.filter(
                                  (item) => !storedRemovedKeys.includes(item.key),
                                );
                                this.setState({ moLines: filteredData || [] });
                              }
                            }}
                          >
                            {/* Add the new "No Product Category" option */}
                            <Option key="" value="null">
                              No Product Category
                            </Option>
                            {optionsForProductCategoryFilter?.map((option) => (
                              <Option key={option.id} value={option.id}>
                                {option?.name}
                              </Option>
                            ))}
                          </PRZSelect>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="form__lines-wrapper">
              {copyMoLines?.length > 0 ? (
                <PurchaseRequestLines
                  handleDelete={this.handleDelete}
                  handleProductChange={this.handleProductChange}
                  handleProductChangeValue={this.handleProductChangeValue}
                  moLines={moLines}
                  copyMoLines={copyMoLines}
                  updateData={(updatedMoLines) => this.setState({ moLines: updatedMoLines })}
                  updateOptionsForProductCategoryFilter={(updatedOptions) => this.setState({ optionsForProductCategoryFilter: updatedOptions })}
                  updateSelectedProductCategory={(updatedOptions) => this.setState({ selectedProductCategory: updatedOptions })}
                  formSubmitted={formSubmitted}
                  loading={createPurchaseRequestLoading}
                  selectedMO={selectedMO}
                  prFromMoScreen
                  cfPurchaseRequestLine={cfPurchaseRequestLine}
                  visibleColumns={visibleColumns}
                  setVisibleColumns={(val) => this.setState({ visibleColumns: val })}
                  customLineInputChange={(fieldValue, key) => {
                    this.customLineInputChange(fieldValue, key)
                  }}
                  isCarryForward
                />
              ) : (
                <PurchaseRequestLines
                  handleDelete={this.handleDelete}
                  handleProductChange={this.handleProductChange}
                  handleProductChangeValue={this.handleProductChangeValue}
                  soLines={soLines}
                  updateData={(updatedSoLines) => this.setState({ soLines: updatedSoLines })}
                  setShowFulfilledLines={(updateFulfilledLines) => this.setState({ showFulfilledLines: updateFulfilledLines })}
                  showFulfilledLines={showFulfilledLines}
                  setIsUserReady={(updateUserReady) => this.setState({ isUserReady: updateUserReady })}
                  isUserReady={isUserReady}
                  formSubmitted={formSubmitted}
                  // selectedMO={selectedMO}
                  loading={createPurchaseRequestLoading || !selectedSO}
                  cfPurchaseRequestLine={cfPurchaseRequestLine}
                  visibleColumns={visibleColumns}
                  setVisibleColumns={(val) => this.setState({ visibleColumns: val })}
                  customLineInputChange={(fieldValue, key) => this.customLineInputChange(fieldValue, key)}
                  isCarryForward
                />
              )}
            </div>
          </div>
        </Fragment>
        <div className="form__footer">
          <Fragment>
            <H3Button
              text="Save as Draft"
              buttonType={defaultButtonTypes.BLUE_ROUNDED}
              customClass="form__footer-draft"
              onClick={() => {
                if (!createPurchaseRequestLoading) {
                  this.createPurchaseRequest(false);
                }
              }}
              isLoading={actionType === 'SAVE_AS_DRAFT' && createPurchaseRequestLoading}
              disabled={createPurchaseRequestLoading || !purchaseRequest}
            />
            <H3Button
              text="Save and Issue"
              buttonType={defaultButtonTypes.BLUE_ROUNDED}
              customClass="form__footer-submit"
              onClick={() => {
                if (!createPurchaseRequestLoading) {
                  this.createPurchaseRequest(true);
                }
              }}
              isLoading={actionType === 'SAVE_AND_ISSUE' && createPurchaseRequestLoading}
              disabled={createPurchaseRequestLoading || !purchaseRequest}
            />
            {!user?.tenant_info?.permission?.editable_material_request_qty_from_mo && (
              <RestrictedAccessMessage message={'You do not have access to edit Quantity of Products on this page'} />
            )}
            {!purchaseRequest && (
              <Popconfirm
                placement="topRight"
                title="This feature is not accessible within your current plan to use this feature contact us."
                onConfirm={() => window.Intercom('showNewMessage')}
                okText="Contact Us"
                cancelText="Cancel"
              >
                <img
                  className="barcode-restrict"
                  src={Crown}
                  alt="premium"
                  style={{
                    marginLeft: '8px',
                    marginTop: '5px',
                  }}
                />
              </Popconfirm>
            )}
          </Fragment>
        </div>
      </Fragment>
    );
  }
}

const mapStateToProps = ({
  UserReducers, PurchaseRequestReducers, MOReducers, CFV2Reducers,
}) => ({
  user: UserReducers.user,
  createPurchaseRequestLoading: PurchaseRequestReducers.createPurchaseRequestLoading,
  selectedMO: MOReducers.selectedMO,
  cfV2DocPurchaseRequest: CFV2Reducers.cfV2DocPurchaseRequest,
});

const mapDispatchToProps = (dispatch) => ({
  createPurchaseRequest: (payload, callback) => dispatch(PurchaseRequestActions.createPurchaseRequest(payload, callback)),
  getDocCFV2: (payload, callback) => dispatch(CFV2Actions.getDocCFV2(payload, callback)),
  createTag: (payload, callback) => dispatch(TagActions.createTag(payload, callback)),

});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(CreatePurchaseRequestFromMo));
